import type {
  CombinedPortfoliosState,
  CombinePortfoliosDraftSettings,
  CombinePortfoliosWizardConfiguration,
  OwnershipSettings,
  TemplateSettings,
} from '../types';
import { noop } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useCombinedPortfolios } from './useCombinedPortfolios';

export type CombinePortfoliosWizardContextValue = {
  configuration: CombinePortfoliosWizardConfiguration;
  combineSettings: CombinePortfoliosDraftSettings;
  ownershipSettings: OwnershipSettings;
  templateSettings: TemplateSettings;
  setCombineSettings: React.Dispatch<React.SetStateAction<CombinePortfoliosDraftSettings>>;
  setOwnershipSettings: React.Dispatch<React.SetStateAction<OwnershipSettings>>;
  setTemplateSettings: React.Dispatch<React.SetStateAction<TemplateSettings>>;
} & CombinedPortfoliosState;

// eslint-disable-next-line react/display-name
export const CombinePortfoliosWizardContext = React.createContext<CombinePortfoliosWizardContextValue>({
  configuration: { destination: 'studio', portfolioInformation: [] },
  combineSettings: {
    structureOption: null,
    organizeOption: 'PORTFOLIO',
    combinedPortfolioName: 'New portfolio',
    includeHousehold: false,
    includeClient: false,
  },
  templateSettings: {
    templateId: null,
    templateName: null,
  },
  ownershipSettings: {},
  setCombineSettings: noop,
  setOwnershipSettings: noop,
  setTemplateSettings: noop,
  portfoliosLoading: true,
  portfolios: null,
});

export const CombinePortfoliosWizardProvider = ({
  children,
  configuration,
}: React.PropsWithChildren<{ configuration: CombinePortfoliosWizardConfiguration }>) => {
  const [combineSettings, setCombineSettings] = useState<CombinePortfoliosDraftSettings>({
    structureOption: configuration.portfolioInformation.length === 1 ? 'SEPARATE' : null,
    organizeOption: 'PORTFOLIO',
    combinedPortfolioName: 'New portfolio',
    includeHousehold: false,
    includeClient: false,
  });

  const [ownershipSettings, setOwnershipSettings] = useState<OwnershipSettings>({});

  const [templateSettings, setTemplateSettings] = useState<TemplateSettings>({
    templateId: null,
    templateName: null,
  });

  const combinedPortfoliosState = useCombinedPortfolios(configuration.portfolioInformation, combineSettings);

  const contextValue = useMemo(
    (): CombinePortfoliosWizardContextValue => ({
      configuration,
      combineSettings,
      ownershipSettings,
      templateSettings,
      setCombineSettings,
      setOwnershipSettings,
      setTemplateSettings,
      ...combinedPortfoliosState,
    }),
    [configuration, combineSettings, ownershipSettings, combinedPortfoliosState, templateSettings],
  );

  return (
    <CombinePortfoliosWizardContext.Provider value={contextValue}>{children}</CombinePortfoliosWizardContext.Provider>
  );
};
